/**
 * Shared name formatting helpers.
 *
 *   1. `titleCaseName(input)` — normalises a name so the first letter of
 *      each word is uppercased and the rest lowercased. Hyphenated and
 *      apostrophe-bearing names ("anna-maria", "o'brien") are handled
 *      correctly. Short all-caps tokens (≤3 chars) like "BBQ" or "DJ"
 *      are preserved.
 *
 *   2. `formatChatDisplayName(input)` — privacy-aware label used on
 *      every chat surface (conversation list, conversation header,
 *      message bubble sender). Returns "Firstname L." so guests and
 *      hosts only ever expose their first name in chats. Works on:
 *        - a string: "nikos papadopoulos"
 *        - a user object: { name } / { first_name, last_name }
 *
 *   Examples:
 *      formatChatDisplayName("nikos papadopoulos")        → "Nikos P."
 *      formatChatDisplayName({ name: "Maria" })           → "Maria"
 *      formatChatDisplayName({ first_name: "anna-maria", last_name: "van der berg" })
 *                                                          → "Anna-Maria V."
 *      formatChatDisplayName(null)                        → ""
 *
 *   Single-word inputs are returned as-is (just title-cased).
 */

export function titleCaseName(input) {
  if (!input) return '';
  const trimmed = String(input).trim().replace(/\s+/g, ' ');
  if (!trimmed) return '';
  return trimmed
    .split(' ')
    .map((word) => {
      if (!word) return word;
      // Preserve short all-caps acronyms (BBQ, DJ, AC).
      if (word.length <= 3 && word === word.toUpperCase() && /^[A-Z]+$/.test(word)) return word;
      // Split on hyphens / apostrophes so "anna-maria" → "Anna-Maria"
      // and "o'brien" → "O'Brien". For 1-character suffixes after an
      // apostrophe (English possessives like "mac's", "d's"), keep the
      // suffix lower-cased so we don't end up with "Mac'S".
      const tokens = word.split(/([-'])/);
      return tokens
        .map((part, idx) => {
          if (part === '-' || part === "'") return part;
          if (!part) return part;
          const prevSep = tokens[idx - 1];
          const isPossessiveSuffix = prevSep === "'" && part.length === 1;
          if (isPossessiveSuffix) {
            return part.toLowerCase();
          }
          return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        })
        .join('');
    })
    .join(' ');
}

export function formatChatDisplayName(input) {
  if (!input) return '';

  // Object input with explicit first_name/last_name fields: build the
  // initial DIRECTLY from the first token of last_name. This is critical
  // for multi-token surnames like "van der berg" where the user's true
  // last initial is "V", not the heuristic "B" you would get by taking
  // the last token of a joined fullName string.
  if (typeof input === 'object' && (input.first_name || input.last_name)) {
    const first = titleCaseName(String(input.first_name || '').trim());
    const lastRaw = String(input.last_name || '').trim();
    if (!first && !lastRaw) return '';
    if (!lastRaw) return first;
    if (!first) return titleCaseName(lastRaw);
    const lastFirstWord = titleCaseName(lastRaw).split(/\s+/).filter(Boolean)[0] || '';
    if (!lastFirstWord) return first;
    return `${first} ${lastFirstWord.charAt(0).toUpperCase()}.`;
  }

  // String / { name } / { username } input: we don't know where the
  // first name ends and the surname begins, so fall back to the
  // heuristic "first token is given name, last token is surname".
  let fullName = '';
  if (typeof input === 'string') {
    fullName = input;
  } else if (typeof input === 'object') {
    if (input.name) {
      fullName = input.name;
    } else if (input.username) {
      fullName = input.username;
    }
  }
  if (!fullName) return '';
  const normalised = titleCaseName(fullName);
  const parts = normalised.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  const first = parts[0];
  const last = parts[parts.length - 1];
  return `${first} ${last.charAt(0).toUpperCase()}.`;
}
